import React, { useState, useEffect } from "react";
import {
	getBetBoomCashbackRequests,
	processBetBoomCashbackRequest,
} from "../../api/apiServiceFetch";
import { DataGrid } from "@mui/x-data-grid";
import {
	Button,
	TextField,
	Box,
	Typography,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
} from "@mui/material";

const STATUS = {
	PENDING: 0,
	PROCESSED: 1,
	FAILED: 2,
};

const statusColor = (status) => {
	switch (status) {
		case STATUS.PROCESSED:
			return "#2e7d32";
		case STATUS.FAILED:
			return "#d32f2f";
		default:
			return "#ed6c02";
	}
};

const CashbackRequests = () => {
	const [rows, setRows] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [statusFilter, setStatusFilter] = useState("");
	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: 20,
	});

	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedRequest, setSelectedRequest] = useState(null);
	const [gamblerId, setGamblerId] = useState("");
	const [isProcessing, setIsProcessing] = useState(false);
	const [error, setError] = useState("");
	const [resultMessage, setResultMessage] = useState("");

	const formatDate = (dateString) => {
		if (!dateString) return "-";
		const date = new Date(dateString);
		return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(
			2,
			"0",
		)}/${String(date.getDate()).padStart(2, "0")} ${String(
			date.getHours(),
		).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
	};

	const fetchRequests = async (page, pageSize, status) => {
		try {
			const response = await getBetBoomCashbackRequests(
				status === "" ? null : status,
				page + 1,
				pageSize,
			);
			setRows(response.data || []);
			setTotalCount(response.totalCount || 0);
		} catch (err) {
			console.error("Error fetching cashback requests:", err);
		}
	};

	useEffect(() => {
		fetchRequests(
			paginationModel.page,
			paginationModel.pageSize,
			statusFilter,
		);
	}, [paginationModel, statusFilter]);

	const openProcessDialog = (row) => {
		setSelectedRequest(row);
		setGamblerId(row.gamblerId || "");
		setError("");
		setResultMessage("");
		setDialogOpen(true);
	};

	const closeDialog = () => {
		setDialogOpen(false);
		setSelectedRequest(null);
		setGamblerId("");
		setError("");
		setResultMessage("");
	};

	const handleProcess = async () => {
		if (!gamblerId.trim()) {
			setError("Укажите gambler_id");
			return;
		}

		try {
			setIsProcessing(true);
			setError("");
			const response = await processBetBoomCashbackRequest(
				selectedRequest.id,
				gamblerId.trim(),
			);
			setResultMessage(response?.message || "Готово");
			await fetchRequests(
				paginationModel.page,
				paginationModel.pageSize,
				statusFilter,
			);
			if (response?.ok) {
				// оставляем окно открытым, чтобы показать результат проверки кешбека
			}
		} catch (err) {
			setError(err.message || "Ошибка обработки");
		} finally {
			setIsProcessing(false);
		}
	};

	const columns = [
		{ field: "id", headerName: "ID", flex: 1 },
		{ field: "userId", headerName: "UserID", flex: 1 },
		{ field: "userFullName", headerName: "Имя", flex: 2 },
		{ field: "phone", headerName: "Телефон", flex: 2 },
		{
			field: "gamblerId",
			headerName: "Gambler ID",
			flex: 2,
			renderCell: (c) => c.row.gamblerId || "-",
		},
		{
			field: "statusText",
			headerName: "Статус",
			flex: 1.5,
			renderCell: (c) => (
				<span style={{ color: statusColor(c.row.status), fontWeight: 700 }}>
					{c.row.statusText}
				</span>
			),
		},
		{
			field: "resultMessage",
			headerName: "Результат",
			flex: 3,
			renderCell: (c) => c.row.resultMessage || "-",
		},
		{
			field: "createdAtUtc",
			headerName: "Создана",
			flex: 2,
			renderCell: (c) => formatDate(c.row.createdAtUtc),
		},
		{
			field: "actions",
			headerName: "Действие",
			flex: 2,
			renderCell: (c) => (
				<Button
					variant="contained"
					color="primary"
					size="small"
					sx={{ "&:focus": { outline: "none" } }}
					onClick={() => openProcessDialog(c.row)}
				>
					Обработать
				</Button>
			),
		},
	];

	return (
		<Box p={3}>
			<Typography variant="h6">Запросы кэшбека</Typography>

			<Box display="flex" alignItems="center" gap={2} my={2}>
				<FormControl size="small" sx={{ minWidth: 200 }}>
					<InputLabel>Статус</InputLabel>
					<Select
						label="Статус"
						value={statusFilter}
						onChange={(e) => {
							setStatusFilter(e.target.value);
							setPaginationModel((prev) => ({ ...prev, page: 0 }));
						}}
					>
						<MenuItem value="">Все</MenuItem>
						<MenuItem value={STATUS.PENDING}>Ожидают</MenuItem>
						<MenuItem value={STATUS.PROCESSED}>Обработаны</MenuItem>
						<MenuItem value={STATUS.FAILED}>Ошибка</MenuItem>
					</Select>
				</FormControl>
			</Box>

			<DataGrid
				rows={rows}
				density="compact"
				getRowId={(row) => row.id}
				hideFooterSelectedRowCount
				columnHeaderHeight={40}
				disableColumnSelector
				rowCount={totalCount}
				sx={{
					"& .MuiDataGrid-cell:focus": { outline: "none" },
					"& .MuiDataGrid-columnHeader:focus": { outline: "none" },
				}}
				pagination
				paginationMode="server"
				paginationModel={paginationModel}
				onPaginationModelChange={setPaginationModel}
				pageSizeOptions={[10, 20, 50]}
				columns={columns}
			/>

			<Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
				<DialogTitle>Обработка заявки</DialogTitle>
				<DialogContent>
					{selectedRequest && (
						<Box mb={2}>
							<div>
								<strong>Пользователь:</strong>{" "}
								{selectedRequest.userFullName || "-"} (ID{" "}
								{selectedRequest.userId})
							</div>
							<div>
								<strong>Телефон:</strong> {selectedRequest.phone}
							</div>
						</Box>
					)}

					<TextField
						label="Gambler ID"
						value={gamblerId}
						onChange={(e) => setGamblerId(e.target.value)}
						fullWidth
						disabled={isProcessing}
						autoFocus
					/>

					{error && (
						<Typography color="error" sx={{ mt: 1 }}>
							{error}
						</Typography>
					)}

					{resultMessage && (
						<Typography sx={{ mt: 1, color: "#2e7d32" }}>
							{resultMessage}
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={closeDialog} color="secondary">
						Закрыть
					</Button>
					<Button
						onClick={handleProcess}
						color="primary"
						variant="contained"
						disabled={isProcessing}
					>
						{isProcessing ? "Обработка..." : "Обработать"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default CashbackRequests;
